import { useIOSPresentation } from '../lib/web-presentation';
import { IOSAccount } from '../ios-web/components';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { PersonaDefinition } from '../types/persona';
import type { PersonaId } from '../types/persona';
import { Brand } from './Brand';
import { StandardAccountMenu } from './StandardAccountMenu';
import { WorkspaceAccountProvider } from './WorkspaceAccountContext';
import { WorkspaceChromeHostProvider, type WorkspaceMobileLayer } from './WorkspaceChromeContext';
import { StandardMenuProvider, useStandardMenu } from './StandardMenu';
import '../styles/standard-menu.css';
import '../styles/standard-header.css';

const mobileLayerTransitionMs = 220;

interface AppShellProps {
  activeProduct: PersonaDefinition;
  actingCapacity?: string;
  availableProducts?: readonly PersonaDefinition[];
  onProductChange?: (product: PersonaId) => void;
  onSignOut: () => void;
  signedInAccount?: { display_name: string; email: string };
  secureAccount?: boolean;
  children: ReactNode;
}

export function AppShell({ activeProduct, actingCapacity, availableProducts = [activeProduct], onProductChange, onSignOut,
  signedInAccount, secureAccount = false, children }: AppShellProps) {
  const ios = useIOSPresentation();
  const menu = useStandardMenu(!ios);
  const displayName = signedInAccount?.display_name ?? activeProduct.label;
  const capacity = actingCapacity ? humanize(actingCapacity) : activeProduct.label;
  const [desktopContextHost, setDesktopContextHost] = useState<HTMLDivElement | null>(null);
  const [mobileContextHost, setMobileContextHost] = useState<HTMLDivElement | null>(null);
  const [mobileIntelligenceHost, setMobileIntelligenceHost] = useState<HTMLDivElement | null>(null);
  const [mobileMenuHost, setMobileMenuHost] = useState<HTMLDivElement | null>(null);
  const [headerIntelligenceHost, setHeaderIntelligenceHost] = useState<HTMLDivElement | null>(null);
  const askControls = useRef(new Map<string, (prompt: string) => void>());
  const settingsControl = useRef<(() => void) | undefined>(undefined);
  const registerAsk = useCallback((scope: string, open: (prompt: string) => void) => {
    askControls.current.set(scope, open);
    return () => { if (askControls.current.get(scope) === open) askControls.current.delete(scope); };
  }, []);
  const requestAsk = useCallback((prompt: string, scope: string) => {
    const open = askControls.current.get(scope); if (!open) return false; open(prompt); return true;
  }, []);
  const registerSettings = useCallback((open: () => void) => {
    settingsControl.current = open;
    return () => { if (settingsControl.current === open) settingsControl.current = undefined; };
  }, []);
  const requestSettings = useCallback(() => {
    if (!settingsControl.current) return false; settingsControl.current(); return true;
  }, []);
  const [activeMobileLayer, setActiveMobileLayer] = useState<WorkspaceMobileLayer | null>(null);
  const activeMobileLayerRef = useRef<WorkspaceMobileLayer | null>(null);
  const pendingMobileLayerRef = useRef<WorkspaceMobileLayer | null>(null);
  const mobileLayerTransitionRef = useRef<number | undefined>(undefined);
  const accountHeaderRef = useRef<HTMLElement>(null);
  const finishMobileLayerTransition = useCallback(() => {
    mobileLayerTransitionRef.current = undefined;
    const pending = pendingMobileLayerRef.current;
    pendingMobileLayerRef.current = null;
    activeMobileLayerRef.current = pending;
    setActiveMobileLayer(pending);
  }, []);
  const beginMobileLayerClose = useCallback((next: WorkspaceMobileLayer | null) => {
    if (mobileLayerTransitionRef.current) window.clearTimeout(mobileLayerTransitionRef.current);
    pendingMobileLayerRef.current = next;
    activeMobileLayerRef.current = null;
    setActiveMobileLayer(null);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    mobileLayerTransitionRef.current = window.setTimeout(finishMobileLayerTransition,
      reducedMotion ? 0 : mobileLayerTransitionMs);
  }, [finishMobileLayerTransition]);
  const openMobileLayer = useCallback((layer: WorkspaceMobileLayer) => {
    if (activeMobileLayerRef.current === layer && !mobileLayerTransitionRef.current) return;
    if (mobileLayerTransitionRef.current) {
      pendingMobileLayerRef.current = layer;
      return;
    }
    if (activeMobileLayerRef.current) {
      beginMobileLayerClose(layer);
      return;
    }
    activeMobileLayerRef.current = layer;
    setActiveMobileLayer(layer);
  }, [beginMobileLayerClose]);
  const closeMobileLayer = useCallback((layer: WorkspaceMobileLayer) => {
    if (activeMobileLayerRef.current !== layer) {
      if (pendingMobileLayerRef.current === layer) pendingMobileLayerRef.current = null;
      return;
    }
    beginMobileLayerClose(null);
  }, [beginMobileLayerClose]);
  useEffect(() => () => {
    if (mobileLayerTransitionRef.current) window.clearTimeout(mobileLayerTransitionRef.current);
  }, []);
  useLayoutEffect(() => {
    const header = accountHeaderRef.current;
    if (!header) return;
    const updateHeight = () => document.documentElement.style.setProperty('--am-mobile-account-header-height', `${header.getBoundingClientRect().height}px`);
    updateHeight();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => {
        window.removeEventListener('resize', updateHeight);
        document.documentElement.style.removeProperty('--am-mobile-account-header-height');
      };
    }
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--am-mobile-account-header-height');
    };
  }, []);
  return (
    <StandardMenuProvider value={menu}><WorkspaceAccountProvider value={{ capacity, displayName, email: signedInAccount?.email, onSignOut, identityInHeader: !ios }}>
      <WorkspaceChromeHostProvider hosts={{ activeMobileLayer, closeMobileLayer, desktopContext: desktopContextHost,
        mobileContext: mobileContextHost, mobileIntelligence: mobileIntelligenceHost, mobileMenu: mobileMenuHost,
        openMobileLayer, headerIntelligence: headerIntelligenceHost, registerAsk, requestAsk, registerSettings, requestSettings }}><div className={`app${menu.active ? ' am-standard-shell' : ''}`} id="top"
          data-menu-stage={menu.active ? menu.stage : undefined} data-menu-dragging={menu.dragging || undefined} style={menu.style}>
      <a className="am-skip-link" href="#product-content">Skip to product content</a>
      <div className="am-standard-menu-host" id="am-standard-menu" ref={menu.setHost}
        aria-hidden={!menu.active || menu.hidden} inert={!menu.active || menu.hidden} />
      {menu.active && menu.device === 'tablet' && menu.stage === 'hidden' && <div aria-hidden="true" className="am-standard-menu-edge" {...menu.dragProps} />}
      <div className="am-standard-page">
      <header className={`account-header${activeMobileLayer === 'account' ? ' account-header--account-open' : ''}`} ref={accountHeaderRef}>
        <div className="account-header__bar">
          {menu.active && <button className="am-standard-menu-trigger" type="button" ref={menu.control}
            {...menu.triggerProps} {...menu.dragProps}><span aria-hidden="true">☰</span></button>}
          <div className="account-header__mobile-menu" ref={setMobileMenuHost} />
          <Brand surface={surfaceName(activeProduct.id)} />
          <div className="account-header__context" ref={setDesktopContextHost} />
          <div className="account-header__actions">
            {ios && secureAccount&&<a href={`/account/settings?product=${activeProduct.id}`}>Security & billing</a>}
            <div className="account-header__mobile-context" ref={setMobileContextHost} />
            <div className="account-header__mobile-intelligence" ref={setMobileIntelligenceHost} />
            {!ios && <div className="account-header__intelligence" ref={setHeaderIntelligenceHost} />}
            {ios ? <IOSAccount activeProduct={activeProduct} availableProducts={availableProducts}
              onProductChange={onProductChange} onSignOut={onSignOut} name={displayName} email={signedInAccount?.email} />
              : <StandardAccountMenu activeProduct={activeProduct} availableProducts={availableProducts} capacity={capacity}
                displayName={displayName} email={signedInAccount?.email} onProductChange={onProductChange}
                onSignOut={onSignOut} secureAccount={secureAccount} surface={surfaceName(activeProduct.id)} />}
          </div>
        </div>
      </header>

      <main className="product-content" id="product-content" tabIndex={-1}>
        <div className="product-content__inner">{children}</div>
      </main>
      </div>
    </div></WorkspaceChromeHostProvider></WorkspaceAccountProvider></StandardMenuProvider>
  );
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ').replaceAll('-', ' ').replace(/^./, (character) => character.toUpperCase());
}

function surfaceName(product: PersonaId): string {
  return product === 'practitioner' ? 'Tax practice' : product === 'business' ? 'Business tax' : 'Personal tax';
}
